import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, Cell, ResponsiveContainer, LabelList } from 'recharts';
import Button from '../UI/Button';

export default function EducationScreen({ product, onContinue }) {
  // Product-specific education screens
  switch(product?.id) {
    case 'nad':
      return <NADEducationScreen onContinue={onContinue} />;
    case 'glutathione':
      return <GlutathioneEducationScreen onContinue={onContinue} />;
    case 'vitaminb12':
      return <B12EducationScreen onContinue={onContinue} />;
    case 'lcarnitine':
      return <LCarnitineEducationScreen onContinue={onContinue} />;
    default:
      return <NADEducationScreen onContinue={onContinue} />;
  }
}

// NAD+: Cellular energy decline with age
function NADEducationScreen({ onContinue }) {
  const chartData = [
    { age: 'Age 20', value: 100, color: '#10b981', label: '100%' },
    { age: 'Age 40', value: 48, color: '#C9A961', label: '48%' },
    { age: 'Age 60', value: 27, color: '#f97316', label: '27%' },
    { age: 'Age 80', value: 15, color: '#ef4444', label: '15%' }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-2xl mx-auto"
    >
      <div className="bg-white rounded-luxury shadow-luxury p-2 md:p-8">
        <h2 className="text-3xl md:text-4xl font-bold text-charcoal text-center mb-6">
          It feels like magic, but it's <span className="text-renuvia-gold">cellular science</span>.
        </h2>

        {/* NAD+ Chart */}
        <div className="bg-warm-beige rounded-xl p-6 md:p-8 mb-8">
          <h3 className="text-center text-charcoal font-semibold text-lg mb-6">NAD+ LEVELS BY AGE</h3>

          <div className="h-72 mb-6">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 30, right: 20, left: -20, bottom: 10 }}>
                <XAxis
                  dataKey="age"
                  tick={{ fill: '#2D2D2D', fontSize: 14 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  domain={[0, 100]}
                  ticks={[0, 25, 50, 75, 100]}
                  tickFormatter={(value) => `${value}%`}
                  tick={{ fill: '#64748b', fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                />
                <Bar
                  dataKey="value"
                  radius={[8, 8, 0, 0]}
                  animationDuration={800}
                  animationBegin={200}
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                  <LabelList
                    dataKey="label"
                    position="top"
                    style={{ fill: '#2D2D2D', fontWeight: 'bold', fontSize: 16 }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-4 text-center">
            <div>
              <div className="text-3xl font-bold text-renuvia-gold mb-1">50%</div>
              <div className="text-slate text-sm">NAD+ decline every 20 years</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-renuvia-gold mb-1">2-4 weeks</div>
              <div className="text-slate text-sm">to feel increased energy</div>
            </div>
          </div>
        </div>

        {/* Benefits text */}
        <div className="mb-8 space-y-4 text-charcoal">
          <p className="text-center">
            On average, NAD+ therapy patients report <strong>improved energy, better mental clarity</strong>, and <strong>enhanced cellular repair</strong>.
          </p>
          <p className="text-center text-slate">
            NAD+ supplementation offers these <strong>powerful anti-aging benefits</strong> – supporting your body's natural cellular processes for optimal health and vitality.
          </p>
        </div>

        <Button onClick={onContinue} fullWidth>
          Continue →
        </Button>
      </div>
    </motion.div>
  );
}

// Glutathione: Systems protected
function GlutathioneEducationScreen({ onContinue }) {
  const systems = [
    { name: 'Skin Health', benefit: 'Brightens & evens skin tone', icon: '✨' },
    { name: 'Liver Support', benefit: 'Detoxifies & protects', icon: '🛡️' },
    { name: 'Immune Function', benefit: 'Strengthens defenses', icon: '💪' },
    { name: 'Brain Health', benefit: 'Protects cognitive function', icon: '🧠' }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-2xl mx-auto"
    >
      <div className="bg-white rounded-luxury shadow-luxury p-8">
        <h2 className="text-3xl md:text-4xl font-bold text-charcoal text-center mb-6">
          The <span className="text-renuvia-gold">master antioxidant</span> your body needs.
        </h2>

        {/* Systems Protected */}
        <div className="bg-warm-beige rounded-xl p-6 md:p-8 mb-8">
          <h3 className="text-center text-charcoal font-semibold text-lg mb-6">WHOLE-BODY PROTECTION</h3>

          <div className="grid grid-cols-2 gap-4 mb-6">
            {systems.map((system, index) => (
              <motion.div
                key={system.name}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-4 text-center"
              >
                <div className="text-4xl mb-2">{system.icon}</div>
                <h4 className="font-semibold text-charcoal mb-1">{system.name}</h4>
                <p className="text-sm text-slate">{system.benefit}</p>
              </motion.div>
            ))}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-4 text-center">
            <div>
              <div className="text-3xl font-bold text-renuvia-gold mb-1">#1</div>
              <div className="text-slate text-sm">Most powerful antioxidant</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-renuvia-gold mb-1">3-4 weeks</div>
              <div className="text-slate text-sm">to notice skin brightening</div>
            </div>
          </div>
        </div>

        {/* Benefits text */}
        <div className="mb-8 space-y-4 text-charcoal">
          <p className="text-center">
            Glutathione is found in every cell of your body, working to <strong>neutralize free radicals, support detoxification</strong>, and <strong>promote radiant skin</strong>.
          </p>
          <p className="text-center text-slate">
            Experience the transformative power of this <strong>essential antioxidant</strong> for whole-body wellness.
          </p>
        </div>

        <Button onClick={onContinue} fullWidth>
          Continue →
        </Button>
      </div>
    </motion.div>
  );
}

// Vitamin B12: Energy curve throughout day
function B12EducationScreen({ onContinue }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-2xl mx-auto"
    >
      <div className="bg-white rounded-luxury shadow-luxury p-8">
        <h2 className="text-3xl md:text-4xl font-bold text-charcoal text-center mb-6">
          Say goodbye to <span className="text-renuvia-gold">afternoon crashes</span>.
        </h2>

        {/* Energy Curve */}
        <div className="bg-warm-beige rounded-xl p-6 md:p-8 mb-8">
          <h3 className="text-center text-charcoal font-semibold text-lg mb-6">SUSTAINED ENERGY ALL DAY</h3>

          <div className="mb-6">
            <svg className="w-full h-48" viewBox="0 0 400 150">
              {/* With B12 - Green line (high and steady) */}
              <motion.path
                d="M 20,40 Q 100,30 200,35 T 380,40"
                stroke="#3D5A4C"
                strokeWidth="4"
                fill="none"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 1.5, delay: 0.2 }}
              />
              {/* Without B12 - Grey line (declining) */}
              <motion.path
                d="M 20,40 Q 100,60 200,90 T 380,130"
                stroke="#9ca3af"
                strokeWidth="4"
                fill="none"
                strokeDasharray="8,8"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 1.5, delay: 0.5 }}
              />

              {/* Labels */}
              <text x="20" y="20" fontSize="12" fill="#3D5A4C" fontWeight="bold">With B12</text>
              <text x="20" y="145" fontSize="12" fill="#9ca3af">Without B12</text>

              <text x="10" y="80" fontSize="11" fill="#64748b">Energy</text>
              <text x="50" y="170" fontSize="11" fill="#64748b">Morning</text>
              <text x="170" y="170" fontSize="11" fill="#64748b">Afternoon</text>
              <text x="320" y="170" fontSize="11" fill="#64748b">Evening</text>
            </svg>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-4 text-center">
            <div>
              <div className="text-3xl font-bold text-renuvia-gold mb-1">100%</div>
              <div className="text-slate text-sm">Better absorption vs pills</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-renuvia-gold mb-1">1-2 weeks</div>
              <div className="text-slate text-sm">to feel sustained energy</div>
            </div>
          </div>
        </div>

        {/* Benefits text */}
        <div className="mb-8 space-y-4 text-charcoal">
          <p className="text-center">
            B12 injections provide <strong>superior absorption</strong> compared to oral supplements, delivering <strong>sustained energy, mental clarity</strong>, and <strong>nervous system support</strong>.
          </p>
          <p className="text-center text-slate">
            Experience the difference of <strong>pharmaceutical-grade B12</strong> that keeps you energized all day long.
          </p>
        </div>

        <Button onClick={onContinue} fullWidth>
          Continue →
        </Button>
      </div>
    </motion.div>
  );
}

// L-Carnitine: Fat-to-energy conversion
function LCarnitineEducationScreen({ onContinue }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-2xl mx-auto"
    >
      <div className="bg-white rounded-luxury shadow-luxury p-8">
        <h2 className="text-3xl md:text-4xl font-bold text-charcoal text-center mb-6">
          Turn fat into <span className="text-renuvia-gold">fuel</span>.
        </h2>

        {/* Metabolic Pathway */}
        <div className="bg-warm-beige rounded-xl p-6 md:p-8 mb-8">
          <h3 className="text-center text-charcoal font-semibold text-lg mb-6">HOW L-CARNITINE WORKS</h3>

          <div className="mb-6">
            <div className="flex items-center justify-center gap-2 md:gap-4">
              {[
                { label: 'Fat Cells', icon: '🔥' },
                { label: 'L-Carnitine Transport', icon: '🚚' },
                { label: 'Mitochondria', icon: '⚡' },
                { label: 'Energy', icon: '💪' }
              ].map((step, index) => (
                <div key={step.label} className="flex items-center gap-2 md:gap-4">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.2 }}
                    className="text-center"
                  >
                    <div className="bg-white rounded-full w-14 h-14 md:w-20 md:h-20 mx-auto flex items-center justify-center mb-2 shadow-md">
                      <span className="text-xl md:text-3xl">{step.icon}</span>
                    </div>
                    <p className="text-xs md:text-sm font-medium text-charcoal max-w-[80px] md:max-w-none">{step.label}</p>
                  </motion.div>

                  {/* Arrow between steps */}
                  {index < 3 && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.2 + 0.3 }}
                      className="text-renuvia-gold text-2xl md:text-3xl font-bold mb-8"
                    >
                      →
                    </motion.div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-4 text-center mt-8">
            <div>
              <div className="text-3xl font-bold text-renuvia-gold mb-1">25%</div>
              <div className="text-slate text-sm">Improved fat oxidation</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-renuvia-gold mb-1">2-3 weeks</div>
              <div className="text-slate text-sm">to notice performance boost</div>
            </div>
          </div>
        </div>

        {/* Benefits text */}
        <div className="mb-8 space-y-4 text-charcoal">
          <p className="text-center">
            L-Carnitine transports fatty acids into your cells' mitochondria, where they're converted into <strong>usable energy</strong> for <strong>enhanced performance and metabolism</strong>.
          </p>
          <p className="text-center text-slate">
            Perfect for athletes and anyone looking to <strong>optimize fat burning and energy production</strong>.
          </p>
        </div>

        <Button onClick={onContinue} fullWidth>
          Continue →
        </Button>
      </div>
    </motion.div>
  );
}
